/** The `product-upsell` block, and the migration off the two blocks it replaces.
 *
 *  WHAT WAS THERE BEFORE
 *  ---------------------
 *  Two `main-cart` blocks had converged on the same strip:
 *
 *    `upsells`   "In-cart recommendations" — products picked on the block
 *                (`settings.handles`, newline-joined), rendered as switches.
 *    `add-ons`   "Cart add-ons" — the same row and the same switch, but its
 *                products came from a store-wide `cart/addons` settings row that
 *                a dashboard page owned. The block carried a heading and nothing
 *                else.
 *
 *  They are now one block, `product-upsell`, configured entirely on the block
 *  instance. The store-wide half is gone: a seller could not answer "what does
 *  THIS theme upsell?" from the builder, and a theme switch carried the
 *  configuration across invisibly.
 *
 *  THE MIGRATION
 *  -------------
 *  `handles` deliberately keeps the id `upsells` used, so the common case is a type
 *  rename with no settings rewrite. Per block list:
 *
 *    - a legacy block becomes `product-upsell` in place, keeping its position;
 *    - if BOTH legacy blocks are present, the first by position survives and the
 *      other is dropped after donating anything the survivor lacks (`add-ons`
 *      only ever had a heading, so in practice: `upsells` keeps its products and
 *      inherits the `add-ons` heading if it had none of its own);
 *    - `help_text` is dropped — it was `add-ons`' pointer at the deleted settings
 *      page, and a stale settings key is how a section silently fails validation
 *      later.
 *
 *  `mergeProductUpsellBlocks` is the primitive, and it is PURE: it clones the
 *  surviving block rather than writing to it. That is what lets the storefront call
 *  it inside a computed — a live store has to render correctly before its seller
 *  next opens the builder, which is the only thing that persists the migration, so
 *  both sides must agree and neither may mutate the other's document.
 *
 *  Products are NOT seeded from the `cart/addons` settings row: it holds variant
 *  ids and the block holds handles, and the theme document is not where a store's
 *  commerce config can be looked up. The `cart.upsell_handles` theme setting still
 *  works as a fallback at render time (see the storefront's `resolveProductUpsell`),
 *  so a store configured that way keeps its strip without this touching it.
 *
 *  Idempotent, like `normalizeSectionWidths`: safe on every builder load.
 */
import { themeSectionLists } from "./section-width.js";
/** The block type everything renders and resolves today. */
export const PRODUCT_UPSELL_TYPE = "product-upsell";
/** Block types that MEAN `product-upsell` in a theme document written before the
 *  merge. Read by the migration here and by the storefront's block host. */
export const PRODUCT_UPSELL_LEGACY_TYPES = ["upsells", "add-ons"];
/** Sections whose block list can hold a `product-upsell`. */
const HOST_SECTIONS = new Set(["main-cart", "main-product"]);
/** Settings keys that existed only on a legacy block and must not survive. */
const DROPPED_SETTINGS = ["help_text"];
export function isLegacyProductUpsellType(type) {
    return PRODUCT_UPSELL_LEGACY_TYPES.includes(type);
}
/** Rewrite one block list so the upsell strip is a single `product-upsell`.
 *
 *  Returns the SAME array reference when there is nothing to migrate, which is
 *  what lets a caller use identity as the "did it change?" test and keeps the
 *  storefront's computed stable across unrelated re-runs. */
export function mergeProductUpsellBlocks(blocks) {
    const legacy = blocks.filter((b) => isLegacyProductUpsellType(b.type));
    if (!legacy.length)
        return blocks;
    // The survivor: an already-migrated block if the list has one (re-running must
    // not mint a second), else the first legacy block by POSITION — the seller
    // ordered the drawer, so position is the one thing here that is their decision.
    const anchor = blocks.find((b) => b.type === PRODUCT_UPSELL_TYPE) ?? legacy[0];
    const donors = new Set(legacy.filter((b) => b !== anchor));
    const settings = { ...anchor.settings };
    for (const d of donors) {
        for (const [k, v] of Object.entries(d.settings ?? {})) {
            // Blank counts as absent — an empty heading on the survivor should not beat
            // a real one on the block being dropped.
            const cur = settings[k];
            if (cur === undefined || cur === "" || cur === null)
                settings[k] = v;
        }
    }
    for (const k of DROPPED_SETTINGS)
        delete settings[k];
    const merged = { ...anchor, type: PRODUCT_UPSELL_TYPE, settings };
    return blocks.filter((b) => !donors.has(b)).map((b) => (b === anchor ? merged : b));
}
/** Migrate one section's block list in place. Returns true when it changed. */
function normalizeSection(section) {
    if (!HOST_SECTIONS.has(section.type) || !section.blocks?.length)
        return false;
    const next = mergeProductUpsellBlocks(section.blocks);
    if (next === section.blocks)
        return false;
    section.blocks = next;
    return true;
}
/** Rewrite every legacy `upsells` / `add-ons` block as `product-upsell`, in place.
 *
 *  Returns true when the document changed, so a caller can decide whether the
 *  migration is worth persisting. */
export function normalizeProductUpsellBlocks(theme) {
    let changed = false;
    for (const list of themeSectionLists(theme)) {
        for (const section of list) {
            changed = normalizeSection(section) || changed;
            for (const child of section.children ?? [])
                changed = normalizeSection(child) || changed;
        }
    }
    return changed;
}
//# sourceMappingURL=product-upsell.js.map